import { BellOutlined, ClockCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import axios from '../api';

const AnnouncementPanel = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        // 尝试从后端获取公告数据
        const response = await axios.get('/api/announcements/');
        setAnnouncements(response.data);
      } catch (error) {
        console.log('使用默认公告数据');
        // 如果后端接口不存在，使用默认数据
        setAnnouncements([
          {
            id: 1,
            title: '系统维护通知',
            content: '系统将于本周日凌晨2:00-4:00进行维护升级，期间可能影响部分功能使用，请提前做好准备。',
            type: 'warning',
            created_at: '2024-01-15T10:30:00Z',
            priority: 'high'
          },
          {
            id: 2,
            title: '新功能上线：智能体管理',
            content: '智能体管理功能已正式上线，支持自定义AI助手配置，提供更专业的服务体验。',
            type: 'info',
            created_at: '2024-01-14T14:20:00Z',
            priority: 'medium'
          },
          {
            id: 3,
            title: '知识库优化完成',
            content: '知识库功能已完成优化，支持更多文件格式，检索速度提升50%，欢迎体验新功能。',
            type: 'success',
            created_at: '2024-01-13T09:15:00Z',
            priority: 'medium'
          },
          {
            id: 4,
            title: '用户权限系统更新',
            content: '用户权限系统已更新，新增细粒度权限控制，管理员可更精确地分配用户权限。',
            type: 'info',
            created_at: '2024-01-12T16:45:00Z',
            priority: 'low'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchAnnouncements();
  }, []);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'warning':
        return <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 16 }} />;
      case 'success':
        return <BellOutlined style={{ color: '#52c41a', fontSize: 16 }} />;
      case 'error':
        return <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />;
      default:
        return <BellOutlined style={{ color: '#1890ff', fontSize: 16 }} />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'warning':
        return '#fff7e6';
      case 'success':
        return '#f6ffed';
      case 'error':
        return '#fff2f0';
      default:
        return '#e6f7ff';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
      return '今天';
    } else if (diffDays === 2) {
      return '昨天';
    } else if (diffDays <= 7) {
      return `${diffDays - 1}天前`;
    } else {
      return date.toLocaleDateString('zh-CN');
    }
  };

  if (loading) {
    return (
      <div style={{
        background: '#fff',
        borderRadius: 16,
        boxShadow: '0 4px 24px #e0e0e0',
        padding: 40,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 200
      }}>
        <div style={{ fontSize: 16, color: '#666' }}>加载公告中...</div>
      </div>
    );
  }

  return (
    <div style={{
      background: '#fff',
      borderRadius: 16,
      boxShadow: '0 4px 24px #e0e0e0',
      padding: 40,
    }}>
      <div style={{
        fontSize: 24,
        fontWeight: 700,
        color: '#333',
        marginBottom: 32,
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12
      }}>
        <BellOutlined style={{ fontSize: 28, color: '#1890ff' }} />
        系统公告
      </div>

      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 16,
        maxHeight: 400,
        overflowY: 'auto',
        paddingRight: 8
      }}>
        {announcements.map(announcement => (
          <div
            key={announcement.id}
            style={{
              background: getTypeColor(announcement.type),
              border: `1px solid ${announcement.type === 'warning' ? '#faad14' : announcement.type === 'success' ? '#52c41a' : '#1890ff'}`,
              borderRadius: 12,
              padding: 20,
              transition: 'all 0.3s ease',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateX(8px)';
              e.target.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateX(0)';
              e.target.style.boxShadow = 'none';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{
                background: '#fff',
                borderRadius: 6,
                padding: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 32,
                minHeight: 32
              }}>
                {getTypeIcon(announcement.type)}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  marginBottom: 8
                }}>
                  <div style={{
                    fontSize: 16,
                    fontWeight: 600,
                    color: '#333'
                  }}>
                    {announcement.title}
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    fontSize: 12,
                    color: '#999'
                  }}>
                    <ClockCircleOutlined />
                    {formatDate(announcement.created_at)}
                  </div>
                  {announcement.priority === 'high' && (
                    <div style={{
                      background: '#ff4d4f',
                      color: '#fff',
                      fontSize: 10,
                      padding: '2px 6px',
                      borderRadius: 4,
                      fontWeight: 600
                    }}>
                      重要
                    </div>
                  )}
                </div>
                <div style={{
                  fontSize: 14,
                  color: '#666',
                  lineHeight: 1.5
                }}>
                  {announcement.content}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {announcements.length === 0 && (
        <div style={{
          textAlign: 'center',
          color: '#999',
          fontSize: 16,
          padding: 40
        }}>
          暂无公告
        </div>
      )}
    </div>
  );
};

export default AnnouncementPanel;
